import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, HTMLAttributes } from "react";
import { useMemo } from "react";

import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// Type definitions for Field components
type FieldOrientation = "vertical" | "horizontal" | "responsive";
type FieldLegendVariant = "legend" | "label";
type FieldErrorItem = { message?: string } | undefined;

type FieldLegendProps = HTMLAttributes<HTMLLegendElement> & {
    className?: string;
    variant?: FieldLegendVariant;
};

type FieldProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    orientation?: FieldOrientation;
};

type FieldErrorProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    children?: React.ReactNode;
    errors?: FieldErrorItem[];
};

const FieldSet = ({ className, ...props }: HTMLAttributes<HTMLFieldSetElement>) => {
    return (
        <fieldset
            data-slot="field-set"
            className={cn(
                "setu:flex setu:flex-col setu:gap-6",
                "setu:has-[>[data-slot=checkbox-group]]:gap-3 setu:has-[>[data-slot=radio-group]]:gap-3",
                className
            )}
            {...props}
        />
    );
};

const FieldLegend = ({ className, variant = "legend", ...props }: FieldLegendProps) => {
    return (
        <legend
            data-slot="field-legend"
            data-variant={variant}
            className={cn(
                "setu:mb-3 setu:font-medium",
                "setu:data-[variant=legend]:text-base",
                "setu:data-[variant=label]:text-sm",
                className
            )}
            {...props}
        />
    );
};

const FieldGroup = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot="field-group"
            className={cn(
                "setu:flex setu:w-full setu:flex-col setu:gap-4 setu:data-[slot=checkbox-group]:gap-3 setu:[&>[data-slot=field-group]]:gap-4",
                className
            )}
            {...props}
        />
    );
};

const fieldVariants = cva("setu:flex setu:w-full setu:gap-3 setu:data-[invalid=true]:text-destructive", {
    variants: {
        orientation: {
            vertical: ["setu:flex-col setu:[&>*]:w-full setu:[&>.sr-only]:w-auto"],
            horizontal: [
                "setu:flex-row setu:items-center setu:justify-center",
                "setu:[&>[data-slot=field-label]]:flex-auto",
                "setu:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
            ],
            responsive: [
                "setu:flex-col setu:[&>*]:w-full setu:[&>.sr-only]:w-auto md:flex-row md:items-center md:[&>*]:w-auto",
                "setu:md:[&>[data-slot=field-label]]:flex-auto",
                "setu:md:has-[>[data-slot=field-content]]:items-start setu:md:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
            ],
        },
    },
    defaultVariants: {
        orientation: "vertical",
    },
});

const Field = ({ className, orientation = "vertical", ...props }: FieldProps & VariantProps<typeof fieldVariants>) => {
    return (
        <div
            role="group"
            data-slot="field"
            data-orientation={orientation}
            className={cn(fieldVariants({ orientation }), className)}
            {...props}
        />
    );
};

const FieldContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot="field-content"
            className={cn("flex flex-1 flex-col gap-1.5 leading-snug", className)}
            {...props}
        />
    );
};

const FieldLabel = ({ className, ...props }: ComponentProps<typeof Label>) => {
    return (
        <Label
            data-slot="field-label"
            className={cn(
                "setu:flex setu:w-fit setu:gap-2 setu:leading-snug setu:group-data-[disabled=true]/field:opacity-50",
                "setu:has-[>[data-slot=field]]:w-full setu:has-[>[data-slot=field]]:flex-col setu:has-[>[data-slot=field]]:rounded-md setu:has-[>[data-slot=field]]:border setu:[&>[data-slot=field]]:p-4",
                "setu:has-data-[state=checked]:bg-primary/5 setu:has-data-[state=checked]:border-primary setu:dark:has-data-[state=checked]:bg-primary/10",
                className
            )}
            {...props}
        />
    );
};

const FieldTitle = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot="field-label"
            className={cn(
                "setu:flex setu:w-fit setu:items-center setu:gap-2 setu:text-sm setu:font-medium setu:leading-snug setu:group-data-[disabled=true]/field:opacity-50",
                className
            )}
            {...props}
        />
    );
};

const FieldDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => {
    return (
        <p
            data-slot="field-description"
            className={cn(
                "setu:text-muted-foreground setu:text-sm setu:font-normal setu:leading-normal setu:group-has-[[data-orientation=horizontal]]/field:text-balance",
                "setu:nth-last-2:-mt-1 setu:last:mt-0 setu:[[data-variant=legend]+&]:-mt-1.5",
                "setu:[&>a:hover]:text-primary setu:[&>a]:underline setu:[&>a]:underline-offset-4",
                className
            )}
            {...props}
        />
    );
};

const FieldSeparator = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            data-slot="field-separator"
            data-content={!!children}
            className={cn("setu:relative setu:-my-2 setu:h-5 setu:text-sm", className)}
            {...props}
        >
            <Separator className="setu:absolute setu:inset-0 setu:top-1/2" />
            {children && (
                <span
                    className="setu:bg-background setu:text-muted-foreground setu:relative setu:mx-auto setu:block setu:w-fit setu:px-2"
                    data-slot="field-separator-content"
                >
                    {children}
                </span>
            )}
        </div>
    );
};

const FieldError = ({ className, children, errors, ...props }: FieldErrorProps) => {
    const content = useMemo(() => {
        if (children) {
            return children;
        }

        if (!errors) {
            return null;
        }

        if (errors?.length === 1 && errors[0]?.message) {
            return errors[0].message;
        }

        return (
            <ul className="setu:ml-4 setu:flex setu:list-disc setu:flex-col setu:gap-1">
                {errors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
            </ul>
        );
    }, [children, errors]);

    if (!content) {
        return null;
    }

    return (
        <div
            role="alert"
            data-slot="field-error"
            className={cn("setu:text-destructive setu:text-sm setu:font-normal", className)}
            {...props}
        >
            {content}
        </div>
    );
};

export {
    Field,
    FieldContent,
    FieldDescription,
    FieldError,
    FieldGroup,
    FieldLabel,
    FieldLegend,
    FieldSeparator,
    FieldSet,
    FieldTitle,
};

// Export types for external use
export type { FieldErrorItem, FieldErrorProps, FieldLegendProps, FieldLegendVariant, FieldOrientation, FieldProps };
